import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PartnershipCTA from "../components/Home/PartnershipCTA";

export default function BusinessesPage() {
  const location = useLocation();

  useEffect(() => {
    document.title = "Our Businesses | EXPGLO BUSINESS PRIVATE LIMITED";
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main>
      {/* HERO SECTION */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-emerald-50/70 via-sky-50/40 to-amber-50/40 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-brand-darkgreen text-xs font-bold uppercase tracking-wider">
            BUSINESS ECOSYSTEM
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Our Unified Business Units
          </h1>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            One corporate umbrella powering multi-disciplinary engines. Designed
            with an extensible architecture allowing seamless addition of future
            venture divisions.
          </p>
        </div>
      </section>

      {/* PARENT ECOSYSTEM HIERARCHY */}
      <section className="py-16 bg-slate-50 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200/80 shadow-sm text-center space-y-8">
            <h3 className="text-xs font-extrabold uppercase tracking-widest text-slate-400">
              PARENT ECOSYSTEM HIERARCHY
            </h3>
            <div className="inline-block p-6 rounded-2xl bg-brand-darkgreen text-white font-extrabold text-xl shadow-lg border-2 border-emerald-500">
              EXPGLO BUSINESS PRIVATE LIMITED (Parent Brand)
            </div>
            <div className="flex justify-center text-slate-300 text-3xl font-black">
              &darr;
            </div>
            <div className="hierarchy-units grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <a
                href="#fund"
                className="p-5 rounded-2xl bg-emerald-50 border border-emerald-300 text-left hover:shadow-md transition block"
              >
                <span className="text-[10px] font-extrabold uppercase text-emerald-700">
                  Active Unit
                </span>
                <h4 className="font-bold text-slate-900 text-base mt-1">
                  EXPGLO FUND
                </h4>
                <p className="text-xs text-slate-500 mt-1">
                  Founder and Investor Ecosystem
                </p>
              </a>
              <a
                href="#school"
                className="p-5 rounded-2xl bg-blue-50 border border-blue-300 text-left hover:shadow-md transition block"
              >
                <span className="text-[10px] font-extrabold uppercase text-blue-700">
                  Active Unit
                </span>
                <h4 className="font-bold text-slate-900 text-base mt-1">
                  EXPGLO STARTUP SCHOOL
                </h4>
                <p className="text-xs text-slate-500 mt-1">
                  Founder Academy and Mentorship
                </p>
              </a>
              <a
                href="#sutrasync"
                className="p-5 rounded-2xl bg-amber-50 border border-amber-300 text-left hover:shadow-md transition block"
              >
                <span className="text-[10px] font-extrabold uppercase text-amber-700">
                  Active Unit
                </span>
                <h4 className="font-bold text-slate-900 text-base mt-1">
                  SutraSync Technologies — Technology & Development Partner
                </h4>
                <p className="text-xs text-slate-500 mt-1">
                  Technology and Innovation
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OUR WORKSPACE / OFFICE SECTION */}
      <section className="relative py-24 lg:py-32 bg-slate-50 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-100/40 blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-100/30 blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* SECTION HEADING */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-brand-green text-xs font-extrabold uppercase tracking-wider">
              OUR WORKSPACE
            </span>

            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950">
              Where Ideas Come to Life.
            </h2>

            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              A modern environment designed for collaboration, innovation and
              the people building tomorrow's businesses.
            </p>
          </div>

          {/* IMAGE BOXES */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="workspace-box overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-lg">
              <img
                src="/assets/images/office.png"
                alt="EXPGLO Business office exterior"
                className="workspace-image block w-full aspect-[4/3] object-cover"
              />
            </div>

            <div className="workspace-box overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-lg">
              <img
                src="/assets/images/OfficeSetup.png"
                alt="EXPGLO Business office interior"
                className="workspace-image block w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BUSINESS UNITS LIST */}
      <section className="py-24 bg-white">
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16"
          id="business-units-list"
        >
          {/* Unit 1: EXPGLO FUND */}
          <div
            className="bg-slate-50 rounded-3xl p-8 sm:p-12 border border-slate-100 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
            id="fund"
          >
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-brand-green text-xs font-extrabold uppercase">
                FOUNDER AND INVESTOR ECOSYSTEM
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900">
                EXPGLO FUND
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                A premier venture syndication arm facilitating direct
                seed-to-series capital deployment. Founders gain access to
                pre-screened angel syndicates, institutional roadshows, and
                structured funding rounds.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-brand-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Startup Pitching Rounds
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-brand-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Investor Syndicate Network
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-brand-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Institutional Seed Grants
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-brand-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Term Sheet Advisory
                </div>
              </div>
              <div className="pt-4 flex flex-wrap gap-3">
                <a
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-bold text-white bg-brand-green hover:bg-brand-darkgreen transition shadow-md"
                  href="https://expglofund.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>Explore Dedicated Fund Overview -&gt;</span>
                </a>
                <Link
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition shadow-sm"
                  to="/fund"
                >
                  <span>View Fund Overview</span>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  alt="EXPGLO Fund Syndicate"
                  className="w-full h-72 object-cover"
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=800&q=80"
                />
              </div>
            </div>
          </div>

          {/* Unit 2: EXPGLO STARTUP SCHOOL */}
          <div
            className="bg-slate-50 rounded-3xl p-8 sm:p-12 border border-slate-100 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
            id="school"
          >
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-extrabold uppercase">
                Turning Ideas Into Opportunities.
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900">
                EXPGLO STARTUP SCHOOL
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                The flagship educational and accelerator division of EXPGLO
                Business. Designed to train founders, researchers, and
                university innovators through rigorous cohorts, customer
                validation sprints, and investor readiness drills.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Startup Fundamentals
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  GTM &amp; Business Development
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Direct Unicorn Mentorship
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Live Pitch Preparation
                </div>
              </div>
              <div className="pt-4 flex flex-wrap gap-3">
                <Link
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-md"
                  to="/contact?type=school"
                >
                  <span>View School Programs &amp; Admissions -&gt;</span>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  alt="Startup School Sessions"
                  className="w-full h-72 object-cover"
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=800&q=80"
                />
              </div>
            </div>
          </div>

          {/* Unit 3: SUTRASYNC */}
          <div
            className="bg-slate-50 rounded-3xl p-8 sm:p-12 border border-slate-100 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
            id="sutrasync"
          >
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-extrabold uppercase">
                TECHNOLOGY &amp; INNOVATION
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900">
                SutraSync Technologies — Technology & Development Partner
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Our specialized technology and innovation division, SutraSync,
                is dedicated to delivering cutting-edge solutions for
                businesses. We focus on digital marketing, growth hacking, web
                and mobile app development, and modern cloud architectures.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Digital Marketing &amp; Growth Hacking
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Web &amp; Mobile App Development
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  Scalable Cloud &amp; DevSecOps
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <svg
                    className="h-4 w-4 shrink-0 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="m5 12 4 4L19 7"
                    ></path>
                  </svg>
                  CMS Development
                </div>
              </div>
              <div className="pt-4 flex flex-wrap gap-3">
                <a
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-bold text-white bg-slate-900 hover:bg-brand-gold hover:text-slate-950 transition shadow-md"
                  href="https://sutrasync.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>Explore SutraSync -&gt;</span>
                </a>
                <Link
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition shadow-sm"
                  to="/contact?type=labs"
                >
                  <span>Contact SutraSync</span>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  alt="SutraSync Technology and Innovation"
                  className="w-full h-72 object-cover"
                  src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnershipCTA />
    </main>
  );
}
